import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';

import { Link, FlexColumn } from 'core/components';
import { ResultsTableV2 } from 'app/components/resultsTable/ResultsTableV2';
import $exports from 'app/stores/$exports';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import $query from 'app/stores/query/$query';
import { applyFiltersToAggFilterQuery } from 'app/components/filters/simple/simpleFilterUtils';
import { cloudProviderData } from 'app/util/utils';

const cloudTypeRenderer = ({ value }) => {
  const cloud = cloudProviderData(value);

  if (!cloud?.id) {
    return value;
  }

  return <Link to={`/v4/core/quick-views/cloud/${cloud.id}`}>{cloud.name}</Link>;
};

const dimensionOverrides = [
  {
    key: 'Cloud',
    name: 'Cloud',
    label: 'Cloud',
    renderer: cloudTypeRenderer,
    minWidth: 260,
    flexBasis: 380
  }
];

const _CloudsTable = ({ loading, dataview, queryModel, results, bucket, tableQuery, fullyLoaded }) => {
  const [initialSort, setSort] = useState({ loading: true });

  useEffect(() => {
    $exports.getSettings().then(({ sortResultsBy: field, sortResultsDir: direction }) => {
      setSort({ field, direction, loading: false });
    });
  }, []);

  const handleSort = useCallback(({ field, direction }) => {
    $exports.setHash({ sortResultsBy: field, sortResultsDir: direction });
  }, []);

  const sortedResults = useMemo(() => {
    const { field, direction, loading: loadingSort } = initialSort;

    if (loading || loadingSort || !fullyLoaded || !results?.size) {
      return null;
    }

    if (field) {
      results.sort(field, direction);
    }

    return results;
  }, [initialSort, results, loading, fullyLoaded]);

  return (
    <FlexColumn height="100%" mt={2}>
      <ResultsTableV2
        showActionsMenu
        query={tableQuery}
        loading={!fullyLoaded}
        dataview={dataview}
        queryModel={queryModel}
        queryResultsCollection={sortedResults}
        bucket={bucket}
        dimensionOverrides={dimensionOverrides}
        onSort={handleSort}
        virtualized
        rowHeight={40}
      />
    </FlexColumn>
  );
};

const CloudsTableInternal = observer(_CloudsTable);

export const CloudsTable = observer(({ lookbackSeconds, aggregateType, filters, device_types }) => {
  const tableQuery = useMemo(() => {
    const query = $query.get('cloudInboundOutboundAggregateQuery', {
      metric: ['kt_cloud_provider'],
      aggregateFiltersDimensionLabel: 'Cloud',
      lookback_seconds: lookbackSeconds,
      aggregateTypes: [aggregateType],
      all_devices: false,
      device_types,
      topx: 350,
      depth: 350
    });

    applyFiltersToAggFilterQuery(query, filters);
    return query;
  }, [lookbackSeconds, aggregateType, filters, device_types]);

  return (
    <LightweightDataViewWrapper query={tableQuery}>
      {(dataProps) => <CloudsTableInternal {...dataProps} tableQuery={tableQuery} />}
    </LightweightDataViewWrapper>
  );
});
